import React, { useEffect, useState } from "react";
import { DashboardLayout } from "../../components/dashboard/DashboardLayout";
import { PageHeader, StatusBadge } from "../../components/dashboard/UI";
import { useI18n } from "../../i18n/I18nContext";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiError } from "../../lib/api";
import {
  Plus, X, Send, ChevronDown, ChevronUp,
} from "lucide-react";

const Tickets = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [newComment, setNewComment] = useState({});
  const [error, setError] = useState("");

  const isClient = user?.role === "client";

  const reload = () =>
    api.get("/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([]));

  useEffect(() => { reload(); }, []);

  const TicketForm = () => {
    const [form, setForm] = useState({
      title: "", description: "", priority: "medium", category: "GENERAL",
    });
    const submit = async (e) => {
      e.preventDefault();
      setError("");
      try {
        await api.post("/tickets", form);
        setShowForm(false);
        await reload();
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
    };
    return (
      <form onSubmit={submit} className="card-sharp p-6 mb-6 space-y-4" data-testid="ticket-form">
        <div className="flex items-center justify-between">
          <p className="tactical-label">{t("dashboard.newTicket")}</p>
          <button type="button" onClick={() => setShowForm(false)} data-testid="ticket-form-close">
            <X className="w-4 h-4 text-white/50" />
          </button>
        </div>
        <input
          required
          placeholder={t("dashboard.ticketTitle")}
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          data-testid="ticket-title-input"
          className="w-full bg-[#0A0D14] border border-white/15 focus:border-[#19A7E0] rounded-sm px-3 py-2.5 text-sm outline-none"
        />
        <textarea
          required
          placeholder={t("dashboard.ticketDescription")}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={4}
          data-testid="ticket-description-input"
          className="w-full bg-[#0A0D14] border border-white/15 focus:border-[#19A7E0] rounded-sm px-3 py-2.5 text-sm outline-none resize-none"
        />
        <div className="grid grid-cols-2 gap-3">
          <select
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            data-testid="ticket-priority-select"
            className="bg-[#0A0D14] border border-white/15 focus:border-[#19A7E0] rounded-sm px-3 py-2.5 text-sm outline-none"
          >
            <option value="low">{t("priorities.low")}</option>
            <option value="medium">{t("priorities.medium")}</option>
            <option value="high">{t("priorities.high")}</option>
            <option value="critical">{t("priorities.critical")}</option>
          </select>
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            data-testid="ticket-category-select"
            className="bg-[#0A0D14] border border-white/15 focus:border-[#19A7E0] rounded-sm px-3 py-2.5 text-sm outline-none"
          >
            <option value="ATLAS">ATLAS</option>
            <option value="SYGNAL_SHIELD">Sygnal Shield</option>
            <option value="RADAR">Radar</option>
            <option value="BIOMETRIC">Biometric</option>
            <option value="GENERAL">General</option>
          </select>
        </div>
        {error && (
          <p className="text-xs text-[#EF4444]" data-testid="ticket-form-error">{error}</p>
        )}
        <button
          type="submit"
          data-testid="ticket-submit-btn"
          className="inline-flex items-center gap-2 btn-primary px-4 py-2.5 text-xs font-mono uppercase tracking-[0.18em] rounded-sm"
        >
          <Send className="w-3.5 h-3.5" /> {t("dashboard.submit")}
        </button>
      </form>
    );
  };

  const submitComment = async (ticketId) => {
    const body = (newComment[ticketId] || "").trim();
    if (!body) return;
    try {
      await api.post(`/tickets/${ticketId}/comments`, { body });
      setNewComment({ ...newComment, [ticketId]: "" });
      await reload();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        eyebrow={t("dashboard.summary")}
        title={t("dashboard.tickets")}
        action={
          isClient && !showForm ? (
            <button
              onClick={() => setShowForm(true)}
              data-testid="new-ticket-btn"
              className="inline-flex items-center gap-2 btn-primary px-4 py-2.5 text-xs font-mono uppercase tracking-[0.18em] rounded-sm"
            >
              <Plus className="w-3.5 h-3.5" /> {t("dashboard.newTicket")}
            </button>
          ) : null
        }
      />
      {showForm && <TicketForm />}

      <div className="space-y-4">
        {tickets.map((tk) => {
          const isOpen = expanded === tk.id;
          return (
            <article key={tk.id} className="card-sharp" data-testid={`ticket-${tk.code}`}>
              <button
                className="w-full text-left p-5 flex items-start justify-between gap-4"
                onClick={() => setExpanded(isOpen ? null : tk.id)}
                data-testid={`ticket-toggle-${tk.code}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[10px] font-mono text-[#19A7E0] tracking-[0.2em]">{tk.code}</p>
                    <StatusBadge status={tk.priority} label={t(`priorities.${tk.priority}`)} />
                    <StatusBadge status={tk.status} label={t(`statuses.${tk.status}`)} />
                    <span className="text-[10px] font-mono text-white/40 tracking-[0.18em] uppercase">
                      {tk.category}
                    </span>
                  </div>
                  <p className="mt-2 text-base font-medium">{tk.title}</p>
                  <p className="mt-1 text-xs font-mono text-white/40">
                    {new Date(tk.updated_at).toLocaleString()}
                  </p>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-white/50" /> : <ChevronDown className="w-4 h-4 text-white/50" />}
              </button>

              {isOpen && (
                <div className="border-t border-white/[0.06] px-5 py-5">
                  <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">{tk.description}</p>
                  <div className="mt-6 space-y-3">
                    {tk.comments.map((c, i) => (
                      <div key={i} className="bg-[#121620] border border-white/[0.06] rounded-sm p-3">
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#19A7E0]">
                          {c.author} · {new Date(c.created_at).toLocaleString()}
                        </p>
                        <p className="mt-2 text-sm">{c.body}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex items-start gap-3">
                    <textarea
                      placeholder={t("dashboard.addComment")}
                      rows={2}
                      value={newComment[tk.id] || ""}
                      onChange={(e) => setNewComment({ ...newComment, [tk.id]: e.target.value })}
                      data-testid={`comment-input-${tk.code}`}
                      className="flex-1 bg-[#0A0D14] border border-white/15 focus:border-[#19A7E0] rounded-sm px-3 py-2 text-sm outline-none resize-none"
                    />
                    <button
                      onClick={() => submitComment(tk.id)}
                      data-testid={`comment-send-${tk.code}`}
                      className="btn-primary px-3 py-2 text-xs font-mono uppercase tracking-[0.18em] rounded-sm inline-flex items-center gap-1.5"
                    >
                      <Send className="w-3.5 h-3.5" /> {t("dashboard.send")}
                    </button>
                  </div>
                </div>
              )}
            </article>
          );
        })}
        {tickets.length === 0 && <p className="text-sm text-white/50">{t("dashboard.noData")}</p>}
      </div>
    </DashboardLayout>
  );
};

export default Tickets;
